import React from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { ArrowUpCircle, Lock, Users, Database, MessageSquare, Zap } from 'lucide-react'

interface UpgradePromptProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  feature?: string
  limitType?: 'members' | 'admins' | 'sms' | 'storage'
  currentPlan?: 'trial' | 'starter' | 'growth' | 'leadership' | 'custom'
  currentUsage?: number
  maxLimit?: number
}

export function UpgradePrompt({
  open,
  onOpenChange,
  feature,
  limitType,
  currentPlan = 'starter',
  currentUsage,
  maxLimit,
}: UpgradePromptProps) {
  const getLimitIcon = () => {
    switch (limitType) {
      case 'members':
        return <Users className="h-6 w-6 text-yellow-500" />
      case 'admins':
        return <Users className="h-6 w-6 text-yellow-500" />
      case 'sms':
        return <MessageSquare className="h-6 w-6 text-yellow-500" />
      case 'storage':
        return <Database className="h-6 w-6 text-yellow-500" />
      default:
        return <Lock className="h-6 w-6 text-yellow-500" />
    }
  }

  const getTitle = () => {
    if (limitType) {
      return `${limitType.charAt(0).toUpperCase() + limitType.slice(1)} Limit Reached`
    }
    if (feature) {
      return `Upgrade Required for ${feature}`
    }
    return 'Upgrade Your Plan'
  }

  const getDescription = () => {
    if (limitType && currentUsage !== undefined && maxLimit !== undefined) {
      return `You've reached your plan limit of ${maxLimit} ${limitType}. You currently have ${currentUsage}. Upgrade to a higher plan to add more.`
    }
    if (feature) {
      const planName = currentPlan === 'starter' ? 'STARTER' : currentPlan === 'growth' ? 'GROWTH' : 'LEADERSHIP'
      return `The ${feature} feature is not available on your current ${planName} plan. Upgrade to access this powerful feature.`
    }
    return 'Upgrade your plan to unlock more features and higher limits.'
  }

  const getRecommendedPlan = () => {
    if (currentPlan === 'trial' || currentPlan === 'starter') {
      return {
        name: 'Growth',
        price: 'GHS 499/month',
        features: [
          'Up to 2,000 members',
          'Unlimited admins',
          'Complete Finance Management',
          'Attendance Tracking',
          'Advanced Ministry Tools',
          '50GB storage',
          '1,000 SMS/month',
        ],
      }
    }
    if (currentPlan === 'growth') {
      return {
        name: 'Leadership',
        price: 'GHS 1,499/month',
        features: [
          'Unlimited members',
          'Unlimited storage',
          'HR & Payroll System',
          'White-label Mobile App',
          'API Access',
          'Unlimited locations',
          '3,000+ SMS/month',
        ],
      }
    }
    return null
  }

  const recommendedPlan = getRecommendedPlan()

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {getLimitIcon()}
            <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            {getDescription()}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {recommendedPlan && (
          <div className="my-4 p-4 bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950 dark:to-sky-950 rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  {recommendedPlan.name} Plan
                  <Badge className="bg-green-500">Recommended</Badge>
                </h4>
                <p className="text-sm text-muted-foreground">{recommendedPlan.price}</p>
              </div>
              <Zap className="h-8 w-8 text-yellow-500" />
            </div>
            <ul className="space-y-2 text-sm">
              {recommendedPlan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <ArrowUpCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Not Now</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              window.location.href = '/settings?tab=subscription'
            }}
            className="bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600"
          >
            <ArrowUpCircle className="h-4 w-4 mr-2" />
            Upgrade Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
